/**
 * Canonical entity definition for Neuro Wellness TMS Centers of America.
 *
 * This is the SINGLE source of truth for how the organization is described
 * across the website — homepage, About, footer, schema, treatment pages,
 * educational content, and physician pages. Every public-facing description
 * should reference or match ENTITY_DESCRIPTION.
 *
 * The authority chain (who developed the technology → who regulates it →
 * who manufactures it → who verifies providers → who delivers treatment)
 * is expressed in AUTHORITY_CHAIN and should be reflected in internal
 * content and outbound citations.
 */

/** Primary production domain. */
export const SITE_URL = "https://neurowellnesstms.com";

/** Canonical clinic name — used verbatim everywhere. */
export const CLINIC_NAME = "Neuro Wellness TMS Centers of America";

/** Canonical entity description (≈45 words). AI/search quotable. */
export const ENTITY_DESCRIPTION =
  "Neuro Wellness TMS Centers of America is an outpatient psychiatric neuromodulation center in Coral Springs, Florida providing advanced treatment options for depression, including SAINT® TMS, conventional TMS, SPRAVATO®, and accelerated neuromodulation options.";

/** Short tagline for hero/footer contexts. */
export const ENTITY_TAGLINE =
  "Outpatient psychiatric neuromodulation center in Coral Springs, Florida.";

/** Primary phone (clickable tel: format and display format). */
export const CLINIC_PHONE_TEL = "9548279793";
export const CLINIC_PHONE_DISPLAY = "954-827-9793";

/** Primary address (single line and structured). */
export const CLINIC_ADDRESS_LINE =
  "9734 West Sample Road, 2nd Floor, Suite B, Coral Springs, FL 33065";
export const CLINIC_STREET = "9734 West Sample Road, 2nd Floor - Suite B";
export const CLINIC_CITY = "Coral Springs";
export const CLINIC_STATE = "FL";
export const CLINIC_ZIP = "33065";

/** Geographic service area. */
export const CLINIC_AREA_SERVED = [
  "Coral Springs, FL",
  "Broward County, FL",
  "Miami-Dade County, FL",
  "Palm Beach County, FL",
  "Fort Lauderdale, FL",
  "Boca Raton, FL",
  "Miami, FL",
];

/**
 * The authority chain for SAINT® TMS — who developed the protocol, who
 * regulates the device, who manufactures it, who verifies providers, and
 * who delivers treatment. This relationship should be made clear through
 * internal content and authoritative outbound citations.
 *
 * Stanford research → FDA clearance → Magnus Medical → Verified SAINT
 * Provider → Neuro Wellness TMS Centers of America → Patient treatment
 */
export interface AuthorityChainStep {
  label: string;
  description: string;
  url?: string;
}

export const AUTHORITY_CHAIN: AuthorityChainStep[] = [
  {
    label: "Stanford University Research",
    description:
      "The SAINT® protocol was developed at the Stanford Brain Stimulation Lab, which published the foundational accelerated TMS research.",
    url: "https://med.stanford.edu/brainstimulationlab.html",
  },
  {
    label: "FDA Clearance",
    description:
      "The Magnus Neuromodulation System with SAINT® Technology received FDA 510(k) clearance (K220177) in September 2022 for adults with major depressive disorder.",
    url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
  },
  {
    label: "Magnus Medical",
    description:
      "Magnus Medical, Inc. manufactures the SAINT® system and maintains the official Verified SAINT® Provider directory.",
    url: "https://www.magnusmedical.com/clinical-research/",
  },
  {
    label: "Verified SAINT® Provider",
    description:
      "Neuro Wellness TMS Centers of America is listed on Magnus Medical's official Verified SAINT® Provider locator — the only verified outpatient SAINT® provider on the East Coast.",
    url: "/find-a-verified-saint-provider",
  },
  {
    label: "Neuro Wellness TMS Centers of America",
    description:
      "An outpatient psychiatric neuromodulation center in Coral Springs, Florida where patients receive the five-day SAINT® treatment protocol.",
  },
];
